import { useEffect, useState, useMemo, useCallback, memo } from 'react';
import { getFromStorage, storeInLocal } from '../../../../../lib/storage';
import { useSelector } from 'react-redux';
import organizationEventEmitter from '../../../../../utilities/organizationEmmiter';
import { Select, Badge } from 'antd';

const OrganizationFilter = memo(({ disabled = false }) => {
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );

  const [selectedOrganizationGlobal, setSelectedOrganizationGlobal] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      if (savedOrg) {
        return JSON.parse(savedOrg);
      } else if (currentPractitioner?.org_id && currentPractitioner?.org_name) {
        const initialOrg = {
          value: currentPractitioner.org_id,
          label: currentPractitioner.org_name,
          org_uuid: currentPractitioner.org_uuid,
        };
        storeInLocal(
          'superAdminSelectedOrganization',
          JSON.stringify(initialOrg)
        );
        return initialOrg;
      }
      return null;
    }
  );

  // Memoize the processed organization data to avoid expensive recalculations
  const organizationDropdown = useMemo(() => {
    if (
      !getActiveOrganizationsList ||
      !Array.isArray(getActiveOrganizationsList)
    ) {
      console.log('OrganizationFilter: No valid organizations data available');
      return [];
    }

    const dataArr = [];
    getActiveOrganizationsList.forEach((key) => {
      // Filter out organizations with empty names as requested
      if (
        key?.org_id &&
        key?.org_name &&
        typeof key.org_name === 'string' &&
        key.org_name.trim() !== ''
      ) {
        dataArr.push({
          value: key?.org_id,
          label: key?.org_name,
          displayLabel: (
            <Badge
              count={key?.user_count || 0} // Default to 0 if user_count is not available
              overflowCount={99}
              offset={[20, 7]}
              style={{ backgroundColor: '#52c41a' }}
              className="mr-2"
              showZero={true}
            >
              <span>{key?.org_name}</span>
            </Badge>
          ),
          org_uuid: key?.org_uuid,
        });
      }
    });
    return dataArr;
  }, [getActiveOrganizationsList]);

  // Create a lookup map for faster search performance
  const organizationLookup = useMemo(() => {
    const lookup = new Map();
    organizationDropdown.forEach((org) => {
      lookup.set(org.value, org.label.toLowerCase());
    });
    return lookup;
  }, [organizationDropdown]);

  useEffect(() => {
    const handleStorageChange = () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      setSelectedOrganizationGlobal(savedOrg ? JSON.parse(savedOrg) : null);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Memoize the organization selection handler
  const handleOrganizationSelect = useCallback(
    (value) => {
      const selectedOrg = organizationDropdown.find(
        (org) => org.value === value
      );

      if (selectedOrg) {
        // Create a clean object without JSX elements for storage
        const cleanSelectedOrg = {
          value: selectedOrg.value,
          label: selectedOrg.label,
          org_uuid: selectedOrg.org_uuid,
        };

        setSelectedOrganizationGlobal(cleanSelectedOrg);
        storeInLocal(
          'superAdminSelectedOrganization',
          JSON.stringify(cleanSelectedOrg)
        );
        organizationEventEmitter.dispatch(
          'organizationChanged',
          cleanSelectedOrg
        );
      }
    },
    [organizationDropdown]
  );

  // Optimized filter function using the lookup map for immediate filtering
  const filterOption = useCallback(
    (input, option) => {
      if (!input) return true;
      const orgName = organizationLookup.get(option.value);
      return orgName ? orgName.includes(input.toLowerCase()) : false;
    },
    [organizationLookup]
  );

  return (
    <div
      id="search_org"
      className={`bg-white shadow-sm rounded-lg w-full sm:w-64 md:w-72 lg:w-80 flex items-center ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
      style={{ height: '32px' }} // Match Ant Design Segmented component height
    >
      <Select
        style={{
          placeholder: {
            fontFamily: 'Poppins',
            color: '#667085',
          },
        }}
        bordered={false}
        className="border-none font-sans flex items-center focus:outline-none w-full"
        size="large"
        filterOption={filterOption}
        virtual={organizationDropdown.length > 100} // Enable virtualization for large lists
        placeholder={disabled ? 'Global' : 'Search organization'}
        showSearch={!disabled}
        disabled={disabled}
        options={organizationDropdown.map((item) => ({
          value: item.value,
          label: item.displayLabel,
        }))}
        value={disabled ? undefined : selectedOrganizationGlobal?.value}
        id="search_org"
        onChange={(e) => !disabled && handleOrganizationSelect(e)}
      ></Select>
    </div>
  );
});

OrganizationFilter.displayName = 'OrganizationFilter';

export default OrganizationFilter;
